// ============================================
// MOTOR DE PRECIFICAÇÃO — CLICK SORVETES
// ============================================

import { parseMoney } from "@/utils/";

// Limita nomes longos
export function ajustarNome(nome) {
  return nome.length > 28 ? nome.slice(0, 28) + "..." : nome;
}

/**
 * CÁLCULO PROFISSIONAL DO PREÇO DE VENDA
 * Baseado em princípios de contabilidade gerencial e financeira.
 */
export function calcularPreco({
  custosSelecionados,
  despesasVarSelecionadas,
  custosFixosSelecionados,
  despesasFixasSelecionadas,
  usarRateio,
  faturamento,
  margem,
}) {
  // ======================================================
  // 1️⃣  VALIDAÇÕES BÁSICAS DE ENTRADA
  // ======================================================

  // Margem de lucro negativa: inviável economicamente.
  if (margem < 0) throw new Error("A margem de lucro não pode ser negativa.");

  // Margem acima de 100%: matematicamente inconsistente.
  if (margem > 100)
    throw new Error("A margem de lucro não pode ultrapassar 100%.");

  // Verifica se o usuário informou algum custo ou despesa variável.
  if (
    (!custosSelecionados.length && !despesasVarSelecionadas.length) ||
    (!custosSelecionados.some((c) => c.valor) &&
      !despesasVarSelecionadas.some((d) => d.valor))
  ) {
    throw new Error(
      "Adicione ao menos um custo ou despesa para formar o preço.",
    );
  }

  // ======================================================
  // 2️⃣  CÁLCULO DE CUSTOS E DESPESAS VARIÁVEIS
  // ======================================================
  // Custos variáveis (moeda) → valores diretos por unidade.
  const totalCV_R$ = custosSelecionados.reduce(
    (acc, item) => (item.tipo === "moeda" ? acc + parseMoney(item.valor) : acc),
    0,
  );

  // Custos variáveis (percentual) → sobre o preço de venda.
  const percCV = custosSelecionados.reduce(
    (acc, item) =>
      item.tipo === "percentual" ? acc + parseMoney(item.valor) : acc,
    0,
  );

  // Despesas variáveis (moeda)
  const totalDV_R$ = despesasVarSelecionadas.reduce(
    (acc, item) => (item.tipo === "moeda" ? acc + parseMoney(item.valor) : acc),
    0,
  );

  // Despesas variáveis (percentual)
  const percDV = despesasVarSelecionadas.reduce(
    (acc, item) =>
      item.tipo === "percentual" ? acc + parseMoney(item.valor) : acc,
    0,
  );

  // ======================================================
  // 3️⃣  RATEIO DE GASTOS FIXOS
  // ======================================================
  let percCustosFixos = 0;
  let percDespesasFixas = 0;

  if (usarRateio) {
    // Exige que o usuário informe o faturamento total
    // para calcular a representatividade percentual dos fixos.
    if (!faturamento || faturamento <= 0)
      throw new Error(
        "Informe o faturamento antes de aplicar o rateio de gastos fixos.",
      );

    // Soma de custos e despesas fixas informadas
    const custosFixosInformados = custosFixosSelecionados.reduce(
      (acc, item) => acc + parseMoney(item.valor),
      0,
    );
    const despesasFixasInformadas = despesasFixasSelecionadas.reduce(
      (acc, item) => acc + parseMoney(item.valor),
      0,
    );

    // Converte para percentual sobre o faturamento informado
    percCustosFixos = (custosFixosInformados / faturamento) * 100;
    percDespesasFixas = (despesasFixasInformadas / faturamento) * 100;
  }

  // ======================================================
  // 4️⃣  VALIDAÇÃO DE PERCENTUAIS TOTAIS
  // ======================================================
  // Soma geral de percentuais sobre o preço (custos + despesas + margem)
  const somaPercentuais =
    percCV + percDV + percCustosFixos + percDespesasFixas + (margem || 0);

  if (somaPercentuais >= 100) {
    // Nenhuma margem de segurança para lucro
    throw new Error(
      "A soma de custos, despesas e margem ultrapassa 100%. Reduza algum percentual para continuar.",
    );
  }

  // ======================================================
  // 5️⃣  CÁLCULO DO PREÇO FINAL DE VENDA
  // ======================================================
  // Denominador contábil: representa a fração restante para o lucro.
  const denom = 1 - somaPercentuais / 100;

  // Fórmula base:
  // Preço de Venda = (Custos Variáveis em R$ + Despesas Variáveis em R$) / (1 - soma dos percentuais)
  const precoFinal = (totalCV_R$ + totalDV_R$) / denom;

  // Converte percentuais em valores monetários proporcionais ao preço final.
  const cvPercentReais = (precoFinal * percCV) / 100;
  const dvPercentReais = (precoFinal * percDV) / 100;
  const custosFixosReais = (precoFinal * percCustosFixos) / 100;
  const despesasFixasReais = (precoFinal * percDespesasFixas) / 100;

  // ======================================================
  // 6️⃣  ANÁLISE DE RESULTADOS MONETÁRIOS
  // ======================================================
  const custosVariaveisTotal = totalCV_R$ + cvPercentReais;
  const despesasVariaveisTotal = totalDV_R$ + dvPercentReais;
  const totalVar = custosVariaveisTotal + despesasVariaveisTotal;
  const totalFixosReais = custosFixosReais + despesasFixasReais;

  // Margem de contribuição — quanto sobra para pagar os fixos.
  const margemContribuicaoMonetaria = precoFinal - totalVar;

  // Margem de lucro — quanto sobra após pagar tudo.
  const margemLucroMonetaria = precoFinal - (totalVar + totalFixosReais);

  // ======================================================
  // 7️⃣  VALIDAÇÕES DE COERÊNCIA ECONÔMICA
  // ======================================================
  if (margemContribuicaoMonetaria < 0) {
    throw new Error(
      "A margem de contribuição é negativa. O preço está abaixo dos custos variáveis — operação inviável.",
    );
  }

  if (precoFinal <= totalCV_R$ + totalDV_R$) {
    throw new Error(
      "O preço de venda não cobre os custos variáveis. É inviável vender por esse valor.",
    );
  }

  // ======================================================
  // 8️⃣  RETORNO DETALHADO
  // ======================================================
  // Inclui análise percentual, útil para gráficos e dashboards.
  const analise = {
    custosVariaveis: (custosVariaveisTotal / precoFinal) * 100,
    despesasVariaveis: (despesasVariaveisTotal / precoFinal) * 100,
    totalVar: (totalVar / precoFinal) * 100,
    custosFixos: (custosFixosReais / precoFinal) * 100,
    despesasFixas: (despesasFixasReais / precoFinal) * 100,
    totalFixos: (totalFixosReais / precoFinal) * 100,
    margemContribuicao: (margemContribuicaoMonetaria / precoFinal) * 100,
    margemLucro: (margemLucroMonetaria / precoFinal) * 100,
  };

  // ======================================================
  // 9️⃣  SAÍDA FINAL
  // ======================================================
  // Retorna os valores em formato estruturado, prontos para renderização no front.
  return {
    precoFinal, // preço de venda ideal
    totais: {
      custosVariaveis: custosVariaveisTotal,
      despesasVariaveis: despesasVariaveisTotal,
      totalVar,
      custosFixos: custosFixosReais,
      despesasFixas: despesasFixasReais,
      totalFixos: totalFixosReais,
    },
    margemContribuicaoMonetaria,
    margemLucroMonetaria,
    analise,
  };
}

// Simulação com barra de progresso
// ============================================
// Simulação com barra de progresso — Click Sorvetes
// ============================================
export function simularProcessamentoGlobal({
  progresso,
  mensagemProgresso,
  carregando,
  processamentoConcluido,
  mostrarDetalhamento,
  calcularPreco,
  resultado,
  showToast,
}) {
  carregando.value = true;
  progresso.value = 0;
  processamentoConcluido.value = false;
  mostrarDetalhamento.value = false;
  mensagemProgresso.value = "Iniciando simulação...";

  // Etapas ilustrativas da barra de progresso
  const etapas = [
    "Analisando informações...",
    "Aplicando conceitos contábeis...",
    "Elaborando cálculos...",
    "Finalizando sua requisição...",
  ];
 
  let etapa = 0;
  const intervalo = setInterval(() => {
    progresso.value += 100 / (etapas.length * 2);

    if (etapa < etapas.length) {
      mensagemProgresso.value = etapas[etapa++];
    }

    // Quando a simulação chega ao fim:
    if (progresso.value >= 100) {
      clearInterval(intervalo);
      carregando.value = false;

      try {
        // 🔹 Executa o cálculo principal
        const res = calcularPreco(); // pode lançar erro, que será tratado abaixo

        resultado.value = res;
        processamentoConcluido.value = true;

        // ===============================
        // ALERTAS PÓS-CÁLCULO (INSIGHTS)
        // ===============================

        // Margem de contribuição negativa
        if (res.analise.margemContribuicao < 0) {
          showToast(
            "⚠️ A margem de contribuição é negativa. O preço de venda não cobre seus custos variáveis.",
            "warning",
          );
        }

        // Margem de lucro negativa
        if (res.analise.margemLucro < 0) {
          showToast(
            "⚠️ O preço cobre os custos variáveis, mas não cobre os gastos fixos. Reveja sua margem desejada.",
            "warning",
          );
        }

        // Estrutura fixa pesada
        if (res.analise.totalFixos > 70) {
          showToast(
            "⚠️ Seus custos e despesas fixas representam mais de 70% do faturamento. Reavalie sua estrutura operacional.",
            "info",
          );
        }

        // Sucesso
        showToast("Preço de venda calculado com sucesso!", "success");
      } catch (error) {
        // ===============================
        // CAPTURA DE ERROS CONTÁBEIS
        // ===============================
        const msg =
          error instanceof Error && error.message
            ? error.message
            : "Erro inesperado ao calcular o preço de venda.";
        showToast(msg, "error");
      }
    }
  }, 1200);
}

// Reset global de campos reativos
export function limparCamposGlobal(refs) {
  Object.values(refs).forEach((r) => {
    if (r.value !== undefined) {
      if (Array.isArray(r.value)) r.value = [];
      else if (typeof r.value === "boolean") r.value = false;
      else if (typeof r.value === "number") r.value = 0;
      else if (typeof r.value === "string") r.value = "";
    }
  });
}
